import React, { useEffect, useState, useMemo } from 'react'
import Loading from '@mortgage-pos/ui/components/Loading'
import InfoModal from '@mortgage-pos/ui/components/InfoModal'
import rollbar from '@mortgage-pos/ui/services/rollbar'
import { useNavigate } from 'react-router-dom'
import LoanOfficerPricing from '@mortgage-pos/ui/components/LoanOfficerPricing'
import newRelic from '@mortgage-pos/ui/services/newRelic'
import Button, { ButtonVariants } from '@mortgage-pos/ui/components/Button'
import { useStoreState, useStoreActions } from '@mortgage-pos/ui/store'
import { lockScroll, unlockScroll } from '@mortgage-pos/utils'
import { PricingDisclosures } from '@mortgage-pos/ui/components/pricing/PricingDisclosures'
import TabbedFeeEstimateModal from '@mortgage-pos/ui/components/TabbedFeeEstimateModal'
import RateCardMasonry, {
  RateCardVariant,
} from '@mortgage-pos/ui/components/pricing/RateCardMasonry'
import { WhyRefiPricingLogic } from '@mortgage-pos/ui/utils/WhyRefiPricingLogic'
import questionMark from '@mortgage-pos/ui/assets/question-mark.svg'
import UpdateRates from '@mortgage-pos/ui/components/UpdateRates'
import incense from '@mortgage-pos/ui/incense'
import useMediaQuery from '@shared-fe/hooks/useMediaQuery'

import {
  LOCardVariant,
  LoanOfficerCard,
} from '@mortgage-pos/ui/components/pricing/LoanOfficerPricingCard'

import {
  LoanPurpose,
  Rate,
  Routes,
  Status,
  RateSelectPageVariation,
} from '@mortgage-pos/types'

const generateAvailableTerms = (
  rates: Rate[],
  matchedRates: Rate[]
): string[] => {
  const availableTerms = rates.reduce((acc, curr) => {
    if (!acc.includes(curr.term)) {
      acc.push(curr.term)
    }
    return acc
  }, [])

  if (matchedRates?.length) {
    availableTerms.unshift('Best Matches')
  }
  return availableTerms
}

const rateTermDisplayMap = {
  F40: '40-year fixed',
  F30: '30-year fixed',
  F25: '25-year fixed',
  F20: '20-year fixed',
  F15: '15-year fixed',
  F10: '10-year fixed',
  A1_1: '1/1 ARM',
  A3_1: '3/1 ARM',
  A5_1: '5/1 ARM',
  A2_1: '2/1 ARM',
  A7_1: '7/1 ARM',
  A10_1: '10/1 ARM',
  A15_1: '15/1 ARM',
  A5_5: '5/5 ARM',
  'Best Matches': 'Best Matches',
}

interface RateSelectionProps {
  modifierClass?: string
}

const LO_CARD_INDEX = 3
const SHOW_MORE_INDEX = 4

const RateSelection = ({ modifierClass }: RateSelectionProps) => {
  const navigate = useNavigate()

  const [isModalOpen, setModalOpen] = useState(false)
  const [modalIndex, setModalIndex] = useState(null)
  const [showDefinitionsModal, setShowDefinitionsModal] = useState(false)
  const [ratesRefreshed, setRatesRefreshed] = useState(false)
  const [showLocards, setShowLoCards] = useState(false)

  const { isRequestingRates, sortedRates, debtToIncome } = useStoreState(
    (state) => state.rates
  )

  const { loanOfficer } = useStoreState((state) => state.loanOfficer)
  const { mergedAnswers } = useStoreState((state) => state.answers)

  const { ratesPage, statusesMap, isSplitFlow } = useStoreState(
    (state) => state.questionnaire
  )

  const { submitRateSelection, requestRates } = useStoreActions(
    (state) => state.rates
  )

  const { formSubmitted } = useStoreActions((state) => state.tracking)
  const { getLoanOfficer } = useStoreActions((state) => state.loanOfficer)
  const { next } = useStoreActions((state) => state.questionnaire)
  const { updateStatus } = useStoreActions((state) => state.application)

  const { refiReason, previouslySelectedRate } = mergedAnswers

  const whyRefiPricingLogic = new WhyRefiPricingLogic(refiReason, {
    sortedRates,
    bankrateSelectedRate: previouslySelectedRate,
  })

  const matchedRates = whyRefiPricingLogic.findMatchedRates()

  const availableTerms = generateAvailableTerms(
    sortedRates,
    matchedRates?.rates
  )

  const [activeTerm, setActiveTerm] = useState(
    availableTerms[0] ? availableTerms[0] : null
  )
  const [showMoreRates, setShowMoreRates] = useState(SHOW_MORE_INDEX)
  const [activeRates, setActiveRates] = useState([])

  const isRefi = mergedAnswers?.loanPurpose === LoanPurpose.Refinance
  const isMobile = useMediaQuery('(max-width: 950px)', true, false)

  const DTI = useMemo(() => {
    if (
      isSplitFlow &&
      mergedAnswers.loanPurpose === LoanPurpose.Purchase &&
      mergedAnswers.purchaseStage === 'contract'
    ) {
      return debtToIncome
    }
  }, [mergedAnswers, isSplitFlow, debtToIncome])

  useEffect(() => {
    setActiveTerm(availableTerms[0])
    setActiveRates(sortedRates.filter((rate) => rate.term === activeTerm))
  }, [sortedRates])

  useEffect(() => {
    setShowMoreRates(SHOW_MORE_INDEX)
    setActiveRates(sortedRates.filter((rate) => rate.term === activeTerm))
  }, [activeTerm, sortedRates])

  const contentDefaults: RateSelectPageVariation = {
    variation: 'default',
    title: 'Choose the rate that best fits your needs',
    subTitle:
      'These are a few rates we found for you, but there are many more options to choose from. Select the one that fits you best and your Loan Officer will reach out to help you make your final selection complete your application.',
    cta: 'Explore this Rate',
  }

  // Use page content coming from Storyblok, with a fallback to default values
  const pageContent = ratesPage?.variations?.[0] || contentDefaults

  useEffect(() => {
    async function getRates() {
      try {
        // @ts-ignore
        const ratesResponse = await requestRates(DTI)

        if (!ratesResponse.length) {
          await updateStatus(statusesMap.noRates)
          return navigate(`/${Routes.APP_1003}/no-rates`)
        }
      } catch (e) {
        await updateStatus(statusesMap.noRates)
        return navigate(`/${Routes.APP_1003}/no-rates`)
      }

      await updateStatus(statusesMap.rates)
    }

    getRates()
  }, [])

  useEffect(() => {
    async function getAssignedLoanOfficer() {
      await getLoanOfficer()
    }

    if (!loanOfficer) {
      getAssignedLoanOfficer()
    }
  }, [])

  const refreshRates = async () => {
    handleGoBackToRatesClick()
    setRatesRefreshed(true)

    try {
      // @ts-ignore
      const ratesResponse = await requestRates()

      if (!ratesResponse.length) {
        return navigate(`/${Routes.APP_1003}/no-rates`)
      }
    } catch (e) {
      incense(e)
        .details({
          name: 'rateSelection.refreshRates',
          message: 'Error while refreshing rates',
        })
        .sensitive({
          mergedAnswers,
        })
        .error()

      return navigate(`/${Routes.APP_1003}/no-rates`)
    }
  }

  const submitUpdatedRate = async (rate: Rate) => {
    try {
      await submitRateSelection(rate)
      formSubmitted()

      next(mergedAnswers)
    } catch (err) {
      rollbar.error('Failed to submit rate', { sensitive: err })
      navigate(`/${Routes.APP_1003}/error`)
    }
  }

  const handleTalkToExpertClick = () => {
    newRelic.increment('ntl_rate_selection.talk_to_an_expert.click')

    setShowLoCards(true)
    updateStatus(Status.CallRequested)
  }

  const handleGoBackToRatesClick = () => {
    setShowLoCards(false)
    setModalOpen(false)
    setModalIndex(null)
    setShowMoreRates(SHOW_MORE_INDEX)
    unlockScroll()
  }

  // OJO Demo stuff ------ <START>
  const { isDemoFlow } = useStoreState((state) => state.leComparison)
  const [isPreApprovalPgShown, setIsPreApprovalPgShown] = useState(false)

  const isAmazonBenefitsFlow = useStoreState(
    (state) => state.questionnaire.isAmazonBenefitsFlow
  )

  function showPreApprovalPage() {
    setIsPreApprovalPgShown(true)
  }

  if (isPreApprovalPgShown) {
    return (
      <div className="rate-selections --ojo-demo">
        <h2 className="rate-selections__header">
          Congratulations! You have been Pre-Qualified
        </h2>

        <h3 className="rate-selections__sub-header">
          Use the link below to download your pre-qualification letter. We at
          Sage Mortgage look forward to helping you along your journey to home
          ownership. Feel free to reach out with any follow-up questions: (980)
          306-5099
        </h3>

        <a
          className="button --from-link"
          href="/assets/robin-testco-prequal.pdf"
          download
        >
          Click to download
        </a>
      </div>
    )
  }
  // OJO Demo stuff ------ <END>

  if (isRequestingRates || !sortedRates.length) {
    return <Loading loaderType="house" shouldWrap={false} />
  }

  return (
    <div className={`rate-selections ${modifierClass || ''}`}>
      <h2
        className="rate-selections__header"
        data-testid="rate-selection-header"
      >
        {pageContent.title}
      </h2>
      <h3 className="rate-selections__sub-header">{pageContent.subTitle}</h3>

      {showDefinitionsModal ? (
        <InfoModal
          onClose={() => setShowDefinitionsModal(false)}
          modalVariant="definitions"
        />
      ) : null}

      {showLocards ? (
        <LoanOfficerPricing
          loanOfficer={loanOfficer}
          handleGoBackToRatesClick={handleGoBackToRatesClick}
        />
      ) : (
        <>
          {ratesRefreshed ? (
            <div className="rate-selections__refresh-info-wrapper">
              <aside className="rate-selections__refresh-info-block">
                We refreshed your pricing options. Please select the rate you
                would like to lock.
                <button onClick={() => setRatesRefreshed(false)}>
                  <img
                    src="@mortgage-pos/ui/assets/black-x.svg"
                    alt="Close button"
                  />
                </button>
              </aside>
            </div>
          ) : null}
          <React.Fragment>
            <div className="rate-selections__update-wrapper">
              {isRefi ? (
                <div className="rate-selections__update-container">
                  <UpdateRates refreshRates={refreshRates} />
                </div>
              ) : null}
            </div>
            <div className="rate-selections__update-container-terms">
              <div className="rate-selections__update-container-terms-buttons">
                {availableTerms.map((term: string) => (
                  <Button
                    className={`rate-terms__term  --pricing-recommendation ${
                      activeTerm === term ? '--active' : ''
                    }
                  `}
                    key={term}
                    variant={ButtonVariants.default}
                    data-testid={`select-button-${term}`}
                    onClick={() => setActiveTerm(term)}
                    content={rateTermDisplayMap[term]}
                  />
                ))}
              </div>
              {!isMobile ? (
                <div
                  className="rate-selections__terms-definition"
                  onClick={() => setShowDefinitionsModal(true)}
                >
                  Terms
                  <img
                    className="rate-selections__question-mark-icon"
                    src={questionMark}
                    alt="question mark icon"
                  />
                </div>
              ) : null}
            </div>
            <React.Fragment>
              <div className="rate-card-container">
                {activeTerm === 'Best Matches'
                  ? matchedRates.rates.map((rate, index) => (
                      <React.Fragment key={index}>
                        <RateCardMasonry
                          handleLockRateClick={
                            isDemoFlow ? showPreApprovalPage : submitUpdatedRate
                          }
                          handleFeeBreakdownClick={() => {
                            setModalOpen(true)
                            setModalIndex(index)
                            lockScroll()
                          }}
                          rate={rate}
                          variant={RateCardVariant.Recommendation}
                          rateType={matchedRates.rateItems[index]}
                          submitButtonText={
                            isAmazonBenefitsFlow
                              ? 'Lock this rate'
                              : 'Explore this rate'
                          }
                          showSuperlativeDescription={isAmazonBenefitsFlow}
                        />

                        {isModalOpen && modalIndex === index ? (
                          <TabbedFeeEstimateModal
                            key={`modal-${index}`}
                            onClose={() => {
                              setModalOpen(false)
                              setModalIndex(null)
                              unlockScroll()
                            }}
                            rate={rate}
                          />
                        ) : null}
                      </React.Fragment>
                    ))
                  : activeRates.map((rate, index) => {
                      if (rate.term !== activeTerm) return null
                      if (index > showMoreRates) return null
                      return (
                        <React.Fragment key={index}>
                          {index === LO_CARD_INDEX ? (
                            <LoanOfficerCard
                              variant={LOCardVariant.card}
                              heading={'Need help?'}
                              body={
                                'Discuss more options with an expert today.'
                              }
                              onCtaClick={handleTalkToExpertClick}
                            />
                          ) : null}
                          <RateCardMasonry
                            handleLockRateClick={
                              isDemoFlow
                                ? showPreApprovalPage
                                : submitUpdatedRate
                            }
                            handleFeeBreakdownClick={() => {
                              setModalOpen(true)
                              setModalIndex(index)
                              lockScroll()
                            }}
                            rate={rate}
                            variant={RateCardVariant.Simple}
                          />

                          {isModalOpen && modalIndex === index ? (
                            <TabbedFeeEstimateModal
                              key={`modal-${index}`}
                              onClose={() => {
                                setModalOpen(false)
                                setModalIndex(null)
                                unlockScroll()
                              }}
                              rate={rate}
                            />
                          ) : null}
                        </React.Fragment>
                      )
                    })}
              </div>
              {activeRates.length > showMoreRates ? (
                <div className={'rate-card-container__show-more-container'}>
                  <Button
                    onClick={() => {
                      setShowMoreRates((n) => n + 6)
                    }}
                    content={'Show more'}
                    variant={ButtonVariants.next}
                    className={'rate-card-container__show-more-btn'}
                  />
                </div>
              ) : null}
            </React.Fragment>
          </React.Fragment>
        </>
      )}

      <PricingDisclosures />
    </div>
  )
}

export default RateSelection
